import { jsx } from 'react/jsx-runtime';
import { createContext, useRef, useContext, useState } from 'react';
import { createStore } from 'jotai/vanilla';
import { useStore, useAtom, useAtomValue, useSetAtom, Provider } from 'jotai/react';
import { useHydrateAtoms } from 'jotai/react/utils';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}

function createIsolation() {
  const StoreContext = createContext(null);
  const Provider = ({
    store,
    initialValues: _initialValues = [],
    children
  }) => {
    const storeRef = useRef(store);
    if (!storeRef.current) {
      storeRef.current = createStore();
    }
    useHydrateAtoms(_initialValues, {
      store: storeRef.current
    });
    return jsx(StoreContext.Provider, {
      value: storeRef.current,
      children: children
    });
  };
  const useStore$1 = options => {
    const store = useContext(StoreContext);
    if (!store) throw new Error('Missing Provider from createIsolation');
    return useStore(_extends({
      store
    }, options));
  };
  const useAtom$1 = (anAtom, options) => {
    const store = useStore$1();
    return useAtom(anAtom, _extends({
      store
    }, options));
  };
  const useAtomValue$1 = (anAtom, options) => {
    const store = useStore$1();
    return useAtomValue(anAtom, _extends({
      store
    }, options));
  };
  const useSetAtom$1 = (anAtom, options) => {
    const store = useStore$1();
    return useSetAtom(anAtom, _extends({
      store
    }, options));
  };
  return {
    Provider,
    useStore: useStore$1,
    useAtom: useAtom$1,
    useAtomValue: useAtomValue$1,
    useSetAtom: useSetAtom$1
  };
}

const isSelfAtom = (atom, a) => atom.unstable_is ? atom.unstable_is(a) : a === atom;
const isEqualSet = (a, b) => a === b || a.size === b.size && Array.from(a).every(v => b.has(v));
const ScopeContext = createContext([a => a, undefined]);
const ScopeProvider = ({
  atoms,
  children
}) => {
  const parentScopeContext = useContext(ScopeContext);
  const atomSet = new Set(atoms);
  const [getParentStoreKey, storeOrUndefined] = parentScopeContext;
  const parentStore = useStore();
  const store = storeOrUndefined != null ? storeOrUndefined : parentStore;
  const initialize = () => {
    const mapping = new WeakMap();
    /**
     * Create a copy of originalAtom, then intercept its read/write function
     * to guarantee it accesses the correct value.
     * @param originalAtom
     * @param markedAsScoped Whether the atom is marked as scoped.
     * @returns A copy of originalAtom.
     */
    const interceptAtom = (originalAtom, markedAsScoped) => {
      /**
       * This is the core mechanism of how an intercepted atom finds the correct
       * atom to read/write.
       *
       * When an scoped atom call get(anotherAtom) or set(anotherAtom, value), this
       * function is called to "route" `anotherAtom` to the correct atom.
       * @param orig The unscoped original atom of this scoped atom.
       * @param target The `anotherAtom` that this atom is accessing.
       * @returns The actual atom to access. If the atom is scoped, return an
       * interceptedAtomCopy. Otherwise, return the unscoped original atom.
       *
       * Check the example below, when calling useAtomValue, jotai-scope will first
       * find its intercepted copy (lets call it `anAtomIntercepted`). Then,
       * `anAtom.read(get => get(dependencyAtom))` becomes
       * `anAtomIntercepted.read(get => get(getAtom(anAtom, dependencyAtom)))`
       * @example
       * const anAtom = atom(get => get(dependencyAtom))
       * const Component = () => {
       *  useAtomValue(anAtom);
       * }
       * const App = () => {
       *   return (
       *    <ScopeProvider atoms={[anAtom]}>
       *      <Component />
       *    </ScopeProvider>
       *   );
       * }
       */
      const getAtom = (orig, target) => {
        // If a target is got/set by itself, then it is not derived.
        // Target could be an intercepted copy, so target is on the left.
        if (isSelfAtom(target, orig)) {
          // Since it is not derived, we check if it is marked as scoped.
          return markedAsScoped ?
          // If it is scoped, then current scope's intercepted copy is the store key
          getInterceptedAtomCopy(target) :
          // Otherwise, find the correct store key in the parent's scope.
          // Check `getStoreKey` for details.
          getParentStoreKey(target);
        }
        // If a target is got/set by another atom, route the access to the
        // target's intercepted copy, then repeat the procedure.
        return getInterceptedAtomCopy(target);
      };
      const interceptedAtomCopy = _extends({}, originalAtom, 'read' in originalAtom && {
        read(get, opts) {
          return originalAtom.read(a => get(getAtom(originalAtom, a)), opts);
        }
      }, 'write' in originalAtom && {
        write(get, set, ...args) {
          return originalAtom.write(a => get(getAtom(originalAtom, a)), (a, ...v) => set(getAtom(originalAtom, a), ...v), ...args);
        }
      }, {
        // eslint-disable-next-line camelcase
        unstable_is: a => isSelfAtom(a, originalAtom)
      });
      return interceptedAtomCopy;
    };
    /**
     * Always create a copy in each scope for each atom, no matter it is marked as
     * scoped or not. Then intercept its read/write function to guarantee it accesses
     * the correct value.
     * @param originalAtom The atom to access.
     * @returns The copy of originalAtom.
     */
    const getInterceptedAtomCopy = originalAtom => {
      let interceptedAtomCopy = mapping.get(originalAtom);
      if (!interceptedAtomCopy) {
        interceptedAtomCopy = atomSet.has(originalAtom) ? interceptAtom(originalAtom, true) : interceptAtom(originalAtom, false);
        mapping.set(originalAtom, interceptedAtomCopy);
      }
      return interceptedAtomCopy;
    };
    /**
     * When a child scope's intercepted atom try to find the correct
     * atom as the store key, this function is called. If the atom
     * is marked as scoped in this scope, return its intercepted copy.
     * Otherwise, recursively find the key in the parent scope.
     * @param originalAtom The atom to access.
     * @returns An intercepted copy if this atom is marked as scoped
     * in this scope. Otherwise, recursively call this function in the
     * parent scope.
     */
    const getStoreKey = originalAtom => {
      if (atomSet.has(originalAtom)) {
        let interceptedAtomCopy = mapping.get(originalAtom);
        if (!interceptedAtomCopy) {
          interceptedAtomCopy = interceptAtom(originalAtom, false);
          mapping.set(originalAtom, interceptedAtomCopy);
        }
        return interceptedAtomCopy;
      }
      return getParentStoreKey(originalAtom);
    };
    /**
     * When an atom is accessed via useAtomValue/useSetAtom, the access should
     * be handled by their intercepted copy.
     */
    const patchedStore = _extends({}, store, {
      get: (anAtom, ...args) => store.get(getInterceptedAtomCopy(anAtom), ...args),
      set: (anAtom, ...args) => store.set(getInterceptedAtomCopy(anAtom), ...args),
      sub: (anAtom, ...args) => store.sub(getInterceptedAtomCopy(anAtom), ...args)
    });
    const scopeContext = [getStoreKey, store];
    return [patchedStore, scopeContext, parentScopeContext, atomSet];
  };
  const [state, setState] = useState(initialize);
  if (parentScopeContext !== state[2] || !isEqualSet(atomSet, state[3])) {
    setState(initialize);
  }
  const [patchedStore, scopeContext] = state;
  return jsx(ScopeContext.Provider, {
    value: scopeContext,
    children: jsx(Provider, {
      store: patchedStore,
      children: children
    })
  });
};

export { ScopeContext as INTERNAL_ScopeContext, ScopeProvider, createIsolation };
//# sourceMappingURL=index.modern.mjs.map
